import React, { useEffect, useRef, useState } from 'react'
import {
  Form,
  FormDataConsumer,
  NumberInput,
  TextInput,
  required,
  useNotify,
  useTranslate,
  useRefresh,
  maxLength,
} from 'react-admin'
import {
  Avatar,
  Chip,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Hidden,
  Icon,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  TableRow,
  TableCell,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { blue, turquoise } from 'src/assets/colors'
import { reservationServices } from 'src/services/sales/reservation/reservation.service'
import { DatePickers } from 'src/components/uiElements/react-hook-fields'
import { Add, CalendarMonthOutlined, Delete, LocationOnOutlined, Save } from '@mui/icons-material'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { LoadingButton } from '@mui/lab'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'
import dayjs from 'dayjs'
import { CardCommonExpandible } from 'src/components/uiElements/cards/CardCommonExpandible'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { ShowElement } from 'src/components/uiElements/CRUD/ShowElement'
import { getSupplierByProduct } from 'src/components/pages/sales/reservation/helper/getSupplierByProduct'
import { customAwait } from 'src/components/pages/sales/reservation/helper/customAwait'
import { CompleteSideCard, LeftSideCard, RigthSideCard } from 'src/components/uiElements/cards/ShowCard'
import { getDay } from '../../helper/getDay'
import { DateInDateOutField } from '../common/DateInDateOut'
import { TrainPassesExtraPassenger } from './TrainPassesExtraPassenger'
import { TrainPassesCondition } from './TrainPassesCondition'
import { useCardId } from 'src/components/pages/sales/reservation/context/CardIdContext'
import { itemServices } from 'src/services/reservation/item/item.service'
import { productServices } from 'src/services/reservation/product/product.service'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import { PRICING } from 'src/components/pages/sales/reservation/logic/common/pricing'
import { INVOICED, CONFIRMED, CANCELLED, EXPIRED, PENDING } from 'src/components/pages/sales/reservation/logic/common/state'
import { tagServices } from 'src/services/reservation/tag/tag.service'
import { FilesCard } from '../common/FilesCard'
import { PERMISSION_GET } from 'src/helper/Permission'
import { HasPermission } from 'src/helper/HasPermission'

const PASSENGER_FIELDS = ['name', 'document_number', 'nationality', 'country']
const NEW_PASSENGER_FIELDS = ['name', 'surname']
const RULE_LIQUIDATION_TYPE = ['WHOLESALER', 'SUPPLIER', 'RETAIL']

export const TrainPassesForm = ({
  openModal,
  reservation,
  passengers,
  workUnit,
  product,
  handleClose,
  onSubmit,
  recordProduct,
}) => {
  const notify = useNotify()
  const translate = useTranslate()
  const refresh = useRefresh()
  const filesCardRef = useRef(null)
  const [record, setRecord] = useState({})
  const [sources, setSources] = useState([])
  const [loadingSources, setLoadingSources] = useState(false)
  const [providers, setProviders] = useState([])
  const [currencies, setCurrencies] = useState([])
  const [currenciesTo, setCurrenciesTo] = useState([])
  const [loadingCurrencies, setLoadingCurrencies] = useState(false)
  const [tags, setTags] = useState([])
  const [loadingTags, setLoadingTags] = useState(false)
  const [timeoutTags, setTimeoutTags] = useState()
  const [aditionalPassengers, setAditionalPassengers] = useState([])
  const [loadingLiquidation, setLoadingLiquidation] = useState(false)
  const [retrieve, setRetrieve] = useState(null)
  const [loadingRetrieve, setLoadingRetrieve] = useState(false)
  const [loadingButton, setLoadingButton] = useState(false)
  const [passengersAux, setPassengersAux] = useState([...passengers])
  const [openModalCondition, setOpenModalCondition] = useState(false)
  const [loadingSupplier, setLoadingSupplier] = useState(false)

  const [wholesalerLiquidation, setWholesalerLiquidation] = useState(null)
  const [retailLiquidation, setRetailLiquidation] = useState(null)
  const [supplierLiquidation, setSupplierLiquidation] = useState(null)
  const { cardId } = useCardId()

  useEffect(() => {
    getSources()
    getCurrencies()
    getTags()
    setRecord({
      number: reservation?.['number'],
      client: reservation?.['business_unit_relation']?.['long_name'],
      amount: 0,
    })
    const providersAux = reservation?.['product']
      ?.find((elem) => elem.ref_table_detail === 'train_detail_segment')
      ?.['supplier_relation']?.map((elem) => {
        const [key, value] = Object.entries(elem)[0]
        return { id: key, name: value }
      })
    let providersFind = null
    ;(async () => {
      try {
        setLoadingSupplier(true)
        providersFind = await getSupplierByProduct(product)
      } catch (error) {
      } finally {
        setLoadingSupplier(false)
      }

      setProviders(providersAux || providersFind)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      if (recordProduct) {
        const sources = await getSourcesService()
        const sourceFind = sources.find((source) => source.id === recordProduct?.source_id)
        const cancelInfoRelationFind = recordProduct?.cancel_info_relation.find(
          (cancel) => !cancel?.is_client_cancel
        )
        setRecord({
          number: reservation?.['number'],
          client: reservation?.['business_unit_relation']?.['long_name'],
          ...recordProduct,
          source: sourceFind,
          cancellation_date: cancelInfoRelationFind?.date,
          cancellation_amount: cancelInfoRelationFind?.amount,
          net_price: recordProduct?.pricing_relation?.[0]?.net_price,
        })
      }
    })()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordProduct])

  const getSourcesService = async () => {
    try {
      const response = await reservationServices.getSources()
      return response
    } catch (error) {
      throw error
    }
  }

  const getBusinessUnitService = async (businessUnitId) => {
    try {
      const response = await businessUnitServices.getById(businessUnitId)
      return response
    } catch (error) {
      throw error
    }
  }

  const getSources = async () => {
    try {
      setLoadingSources(true)
      const response = await reservationServices.getSources()
      setSources(response)
      setLoadingSources(false)
    } catch (error) {
      setSources([])
      setLoadingSources(false)
    }
  }

  const getCurrencies = async () => {
    try {
      setLoadingCurrencies(true)
      if (!reservation?.business_unit_id){
        setCurrencies([])
        return
      }
      const response = await getBusinessUnitService(reservation?.business_unit_id)
      if (response) {
        const allCurrencies = [...response.currency_relation, ...response.currency_inheritance]
        const currenciesAux = []
        const currenciesToAux = []
        allCurrencies?.forEach((currency) => {
          if (!currenciesAux.find((elem) => elem.id === currency.currency_id)) {
            currenciesAux.push(currency.currency_relation)
          }
          if (
            !currenciesToAux.find(
              (elem) =>
                elem.currency_id === currency.currency_id && elem.currency_to_id === currency.currency_to_id
            )
          ) {
            currenciesToAux.push(currency)
          }
        })
        setCurrencies(currenciesAux)
        setCurrenciesTo(currenciesToAux)
      }
      setLoadingCurrencies(false)
    } catch (error) {
      setCurrencies([])
      setLoadingCurrencies(false)
    }
  }

  const getTags = async (name) => {
    clearTimeout(timeoutTags)
    try {
      const timeout = setTimeout(async () => {
        setLoadingTags(true)
        const tagsResponse = await tagServices.getTagsByWorkUnit(reservation?.['work_unit_id'], name)
        setTags(tagsResponse)
        setLoadingTags(false)
      }, 1000)
      setTimeoutTags(timeout)
    } catch (error) {
      setTags([])
      setLoadingTags(false)
    }
  }

  const onClickCalculateLiquidationAux = async (formData, rule_liquidation_type, currency_to_show = null) => {
    let productVar = null
    let checkin = retrieve?.passes?.[0]?.date_in
    if (recordProduct) {
      productVar = await productServices.getProductByWorkUnitWithId(product?.product_by_work_unit_id)
      checkin = recordProduct?.passes?.[0]?.date_in
    }
    let data = {
      token_workunit: workUnit?.token,
      token_product: product?.token_id || productVar?.token_id,
      hash_product: product?.product_relation?.hash_product || productVar?.product_relation?.hash_product,
      rules_parameters: {
        business_unit_token: reservation?.business_unit_relation?.token,
        destination_type: 1,
        checkin: checkin ? dayjs(checkin).format('YYYY-MM-DD') : null,
        rule_type: 'LIQUIDATION',
        rule_liquidation_type,
        supplier: providers.find(elem => elem.id === formData?.supplier_id)?.code || null
      },
      data: {
        ...PRICING,
        final_sale: formData.final_sale || 0,
        currency_to_show: currency_to_show || {}
      }
    }

    if (retrieve) {
      data.data = {
        ...data.data,
        ...retrieve.pricing?.[0],
      }
    }

    try {
      const response = await reservationServices.applyLiquidationRule(data)
      return {
        liquidation: response,
        rule_liquidation_type,
        requestRule: data,
        message: '',
        messsageArg: '',
        rule_applied_info: response?.rules
      }
    } catch (error) {
      return {
        liquidation: {},
        rule_liquidation_type,
        requestRule: data,
        message: 'ra.message.rule_not_apply',
        messsageArg: 'liquidación',
      }
    }
  }

  const onClickCalculateLiquidation = async (formData) => {
    try {
      setLoadingLiquidation(true)

      const currency_to_show = {}
      currencies
        .forEach((currency) => {
          if (currency.id !== formData.currency_id) {
            const currencyToRelation = currenciesTo.find(
              (currencyTo) =>
                (currencyTo.currency_id === formData.currency_id &&
                  currencyTo.currency_to_id === currency.id) ||
                (currencyTo.currency_id === currency.id &&
                  currencyTo.currency_to_id === formData.currency_id &&
                  currencyTo.is_reverse)
            )
            if (currencyToRelation) {
              currency_to_show[currency.short_name] =
                currencyToRelation.currency_id === formData.currency_id
                  ? currencyToRelation.value
                  : Math.round((1 / currencyToRelation.value) * 10000) / 10000
            }
          } else {
            currency_to_show[currency.short_name] = 1
          }
        })
      const responses = await Promise.all(
        RULE_LIQUIDATION_TYPE.map((type) => onClickCalculateLiquidationAux(formData, type, currency_to_show))
      )

      const pricing = { ...formData.pricing }
      responses.forEach((elem) => {
        const rules = elem?.liquidation?.data?.item?.rules
        if (rules) {
          elem['liquidation']['rules'] = rules
        }
        pricing[`liquidation_${elem.rule_liquidation_type.toLowerCase()}`] = { ...elem }
      })
      pricing.currency_origin = currencies.find((elem) => elem.id === formData.currency_id)?.short_name || ''

      setWholesalerLiquidation(
        pricing.liquidation_wholesaler.liquidation.rules?.customVars?.filter((elem) => elem.show) || []
      )

      setRetailLiquidation(
        pricing.liquidation_retail.liquidation.rules?.customVars?.filter((elem) => elem.show) || []
      )

      setSupplierLiquidation(
        pricing.liquidation_supplier.liquidation.rules?.customVars?.filter((elem) => elem.show) || []
      )

      pricing.currency_to_show = currency_to_show

      setRecord({
        ...formData,
        pricing,
      })
      return {
        ...formData,
        pricing,
      }
    } catch (error) {
      console.log('onClickCalculateLiquidation error', error)
    } finally {
      setLoadingLiquidation(false)
    }
  }

  const getRetrieve = async (formData) => {
    try {
      setLoadingRetrieve(true)
      const response = await reservationServices.getRetrieveProduct(
        formData.externalId,
        product.id,
        reservation.id
      )
      response.pax_info.forEach((elem, i) => {
        elem.id = elem.id || `id-${i}`
        if (elem.document_number && typeof elem.document_number !== 'string') {
          elem.document_number = `${elem.document_number}`
        }
        if (elem.extra?.documentNumber && typeof elem.extra?.documentNumber !== 'string') {
          elem.extra.documentNumber = `${elem.extra.documentNumber}`
        }
      })

      setPassengersAux([...passengers, ...response.pax_info])
      setRecord({
        ...formData,
        booking_reference: response.booking_reference,
        currency_id: currencies.find((elem) => elem.short_name === response.pricing?.[0]?.currency)?.id || '',
        status: response.status || INVOICED,
        source: sources.find((elem) => elem.long_name === 'OFFLINE') || null,
        net_price: response.pricing?.[0]?.net_price || 0,
        supplier_id: providers.find((elem) => elem.code === 'RAILEUROPE')?.id || '',
        cancellation_amount: response.pricing?.[0]?.fee || 0,
        pax_titular: response.pax_info?.[0]?.id || formData.pax_titular || '',
        pax_info: response.pax_info?.[0]?.id || formData.pax_info || '',
        cancellation_date: dayjs(),
      })
      setRetrieve(response)
      setLoadingRetrieve(false)
    } catch (error) {
      setRetrieve(null)
      setPassengersAux([...passengers])
      setRecord({ ...formData })
      setLoadingRetrieve(false)
      notify('ra.notification.error_retrieve', { type: 'error' })
    }
  }

  const getAvailablePassengers = (formData, filterAditional = false, aditionalPassengerId = null) => {
    let passengerIds = [formData?.pax_info]

    if (filterAditional) {
      passengerIds = [...passengerIds, ...aditionalPassengers.map((elem) => elem.id && !elem.isNew)]
    }

    if (aditionalPassengerId) {
      passengerIds = passengerIds.filter((elem) => elem !== aditionalPassengerId)
    }

    return passengersAux.filter((passenger) => !passengerIds.includes(passenger.id))
  }

  const onChangeTitularPassenger = (passenger, formData) => {
    const formDataAux = JSON.parse(JSON.stringify(formData))
    formDataAux.pax_info = passenger
    setRecord(formDataAux)

    const aditionalPassengersAux = [...aditionalPassengers]
    const indexPassenger = aditionalPassengersAux.findIndex((elem) => elem.id === passenger)
    if ((!isNaN(passenger) || !passenger.includes('new')) && indexPassenger > -1) {
      aditionalPassengersAux.splice(indexPassenger, 1)
      setAditionalPassengers(aditionalPassengersAux)
    }
  }

  const onChangePassenger = (value, index, field) => {
    const aditionalPassengersAux = [...aditionalPassengers]
    aditionalPassengersAux[index][field] = value
    setAditionalPassengers(aditionalPassengersAux)
  }

  const onChangeAditionalPassenger = (passenger, index) => {
    const aditionalPassengersAux = [...aditionalPassengers]
    aditionalPassengersAux[index] = passengers.find((elem) => elem.id === passenger)
    setAditionalPassengers(aditionalPassengersAux)
  }

  const addPassenger = (isNew = false) => {
    const index =
      parseInt(
        aditionalPassengers
          ?.filter((elem) => elem.isNew)
          ?.map((elem) => elem.id.split('_')[1])
          ?.reverse()?.[0] || -1
      ) + 1
    setAditionalPassengers([
      ...aditionalPassengers,
      {
        isNew,
        id: `new_${index}`,
      },
    ])
  }

  const removePassenger = (index) => {
    const aditionalPassengersAux = [...aditionalPassengers]
    aditionalPassengersAux.splice(index, 1)
    setAditionalPassengers(aditionalPassengersAux)
  }

  const getPassengerInfo = (passengerId, field) => {
    const allPassengers = [
      ...passengersAux,
      ...aditionalPassengers.filter((elem) => elem.isNew && elem.name && elem.surname),
    ]
    const passenger = allPassengers.find((elem) => elem.id === passengerId)
    if (passenger && field === 'name') {
      return `${passenger.name} ${passenger.surname}`
    } else {
      return passenger?.[field] || ''
    }
  }

  const getPassenger = (passengerId) => {
    if (!isNaN(passengerId)) {
      return passengerId
    } else {
      const passenger = { ...aditionalPassengers.find((elem) => elem.id === passengerId) }
      delete passenger.id
      delete passenger.isNew
      return passenger
    }
  }

  const getPassengersToSend = (data, addAditional = true, deleteId = true) => {
    const passenger =
      data.pax_info && !isNaN(data.pax_info) ? [passengersAux.find((elem) => elem.id === data.pax_info)] : []
    const aditionalPassengersAux = addAditional
      ? aditionalPassengers?.map((elem) => getPassenger(elem.id))
      : []
    const retrievePassengers =
      retrieve?.pax_info?.filter((elem) => !passenger.find((elemAux) => elem.id === elemAux.id)) || []

    let passengersToSend = [...passenger, ...aditionalPassengersAux, ...retrievePassengers]
    passengersToSend = JSON.parse(JSON.stringify(passengersToSend))

    if (deleteId) {
      passengersToSend.forEach((elem) => {
        if (typeof elem.id === 'string' && elem.id.includes('id-')) {
          delete elem.id
        }
      })
    }

    return passengersToSend
  }

  const postSave = async (data) => {
    const allPassengers = [
      ...passengersAux,
      ...aditionalPassengers.filter((elem) => elem.isNew && elem.name && elem.surname),
    ]
    let paxTitular = allPassengers.find((elem) => elem.id === data.pax_titular) || {}
    if (
      typeof paxTitular.id === 'string' &&
      (paxTitular.id.includes('id-') || paxTitular.id.includes('new'))
    ) {
      paxTitular = JSON.parse(JSON.stringify(paxTitular))
      delete paxTitular.id
      delete paxTitular.isNew
    }

    let productVar = null
    if (recordProduct) {
      productVar = await productServices.getProductByWorkUnitWithId(product?.product_by_work_unit_id)
    }

    const dataToSend = {
      ...data,
      product_id: product?.product_id,
      product_token: product?.token_id || productVar?.token_id,
      ref_table_detail: 'train_detail_passes',
      reservation_id: reservation.id,
      pax_titular: paxTitular,
      tag_relation: data.tag_relation.map((elem) => ({ id: elem.id })),
      cancellation_date: data.cancellation_date ? dayjs(data.cancellation_date).format('YYYY-MM-DD') : null,
      source_id: data.source.id,
      is_retrieve: true,
      ...retrieve,
      pax_info: getPassengersToSend(data),
    }

    const pricing_retrieve = {}
    for (const key in retrieve.pricing[0]) {
        if (retrieve.pricing[0][key] !== null) {
            pricing_retrieve[key] = retrieve.pricing[0][key]
        }
    }

    const dataToSendEdit = {
      ...data,
      id: data?.item_id,
      destination_type: data?.destination_type,
      product_token: product?.token_id || productVar?.token_id,
      source_id: data.source.id,
      cancellation_date: data.cancellation_date ? dayjs(data.cancellation_date).format('YYYY-MM-DD') : null,
      cancel_info_relation: data?.cancel_info_relation?.map((cancel) => {
        if (!cancel?.is_client_cancel) {
          return {
            ...cancel,
            date: data.cancellation_date ? dayjs(data.cancellation_date).format('YYYY-MM-DD') : null,
            amount: data?.cancellation_amount,
          }
        }
        return cancel
      }),
      detail: {
        id: data?.id_detail,
        supplier_id: data.supplier_id,
      },
    }

    setLoadingButton(true)
    try {
      let response
      if (recordProduct) {
        const liquidationCalculate = await onClickCalculateLiquidation(dataToSendEdit)
        if (liquidationCalculate) {
          dataToSendEdit['pricing_relation'] = [
            {
              ...liquidationCalculate?.pricing_relation?.[0],
              ...liquidationCalculate?.pricing,
            },
          ]
        }
        response = await itemServices.update_item_and_detail(dataToSendEdit.id, dataToSendEdit)
      } else {
        const liquidationCalculate = await onClickCalculateLiquidation(data)
        dataToSend.pricing = {
          ...PRICING,
          ...(liquidationCalculate?.pricing || data.pricing || {}),
          ...pricing_retrieve,
          final_sale: data.final_sale || 0
        }
        response = await reservationServices.createItemManual(dataToSend)

        if (filesCardRef?.current) {
          filesCardRef.current.uploadPendingFiles(response.hash_)
        }
      }

      setLoadingButton(false)
      onSubmit && onSubmit(response, cardId)

      if (recordProduct) {
        notify(translate('ra.message.success_edit_item'), { type: 'success' })
      } else {
        notify(translate('ra.message.success_create_item'), { type: 'success' })
      }
    } catch (error) {
      if (!recordProduct) {
        notify(translate('ra.message.error_create_item'), { type: 'error' })
      } else {
        notify(translate('ra.message.error_update_item'), { type: 'error' })
      }

      setLoadingButton(false)
    } finally {
      customAwait([refresh])
      handleClose()
    }
  }

  const onChangeNetPrice = (formData, net_price) => {
    setRecord({
      ...formData,
      net_price,
      cancellation_amount: net_price,
    })
  }

  const getText = (item, optionsText) => (optionsText ? optionsText.map((option) => item[option]) : item.name)

  const renderOption = (props, option) => (
    <li {...props}>
      <Chip
        variant='outlined'
        label={getText(option, ['long_name'])}
        avatar={option?.symbol && <Avatar>{option?.symbol}</Avatar>}
        style={{ backgroundColor: option?.color, cursor: 'pointer' }}
        size='small'
      />
    </li>
  )

  return (
    <Form id='post_create_form' warnWhenUnsavedChanges onSubmit={postSave} record={record}>
      <Dialog open={openModal} maxWidth='xl' fullWidth={true} className='dialog-product'>
        <DialogTitle>{`${translate('ra.action.add')} ${translate('ra.title.train')}`}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={recordProduct ? 12 : 9}>
                {!recordProduct && (
                  <>
                    <CardCommon
                      title={translate('ra.message.retrieve_supplier_data')}
                      style={{ marginBottom: 12 }}
                    >
                      <Grid container spacing={3}>
                        <EditInputsWrap
                          label={translate('ra.field.hash_booking_id')}
                          required
                          xs={24}
                          md={24}
                          className='small'
                        >
                          <TextInput source='externalId' label='' fullWidth validate={[required()]} />
                        </EditInputsWrap>
                        <Grid item xs={12} md={6}>
                          <FormDataConsumer>
                            {({ formData }) => (
                              <LoadingButton
                                variant='contained'
                                onClick={() => getRetrieve(formData)}
                                disabled={!formData.externalId}
                                loading={loadingRetrieve}
                              >
                                {translate('ra.message.retrieve_supplier_data')}
                              </LoadingButton>
                            )}
                          </FormDataConsumer>
                        </Grid>
                      </Grid>
                    </CardCommon>
                    <CardCommon title={translate('ra.title.passenger')} style={{ marginBottom: 12 }}>
                      <FormDataConsumer>
                        {({ formData }) => (
                          <Grid container spacing={3}>
                            {getPassengersToSend(formData, false, false)?.map((passenger, z) => (
                              <Grid item xs={12} key={z} className='passenger-container'>
                                {PASSENGER_FIELDS.map((field, j) => (
                                  <EditInputsWrap
                                    label={translate(`ra.field.${field}`)}
                                    md={j === 3 ? 2 : 3}
                                    key={j}
                                  >
                                    <TextField
                                      label=''
                                      size='small'
                                      fullWidth
                                      disabled
                                      value={getPassengerInfo(passenger.id, field)}
                                    />
                                  </EditInputsWrap>
                                ))}
                              </Grid>
                            ))}
                            {aditionalPassengers.map((passenger, z) => (
                              <Grid item xs={12} key={z} className='passenger-container'>
                                {passenger.isNew
                                  ? NEW_PASSENGER_FIELDS.map((field, j) => (
                                      <EditInputsWrap label={translate(`ra.field.${field}`)} md={3} key={j}>
                                        <TextField
                                          label=''
                                          size='small'
                                          fullWidth
                                          onChange={(e) => onChangePassenger(e.target.value, z, field)}
                                        />
                                      </EditInputsWrap>
                                    ))
                                  : PASSENGER_FIELDS.map((field, j) => (
                                      <EditInputsWrap
                                        label={translate(`ra.field.${field}`)}
                                        lg={3}
                                        md={j === 3 ? 2 : 3}
                                        key={j}
                                      >
                                        {field === 'name' ? (
                                          <Select
                                            fullWidth
                                            size='small'
                                            value={passenger.id}
                                            onChange={(e) => onChangeAditionalPassenger(e.target.value, z)}
                                          >
                                            {getAvailablePassengers(formData, passenger.id).map(
                                              (passenger, i) => (
                                                <MenuItem key={i} value={passenger.id}>
                                                  {getPassengerInfo(passenger.id, field)}
                                                </MenuItem>
                                              )
                                            )}
                                          </Select>
                                        ) : (
                                          <TextField
                                            label=''
                                            size='small'
                                            fullWidth
                                            disabled
                                            value={getPassengerInfo(passenger.id, field)}
                                          />
                                        )}
                                      </EditInputsWrap>
                                    ))}
                                <Grid
                                  item
                                  xs={passenger.isNew ? 6 : 1}
                                  style={{ paddingTop: 25, textAlign: 'end' }}
                                >
                                  <IconButton onClick={() => removePassenger(z)} color='error'>
                                    <Delete />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            ))}
                            <Grid item xs={12}>
                              <Button
                                variant='standard'
                                startIcon={<Add />}
                                style={{ color: blue }}
                                onClick={() => addPassenger(true)}
                              >
                                Crear un pasajero
                              </Button>
                              <Button
                                variant='standard'
                                startIcon={<Add />}
                                style={{ color: blue }}
                                onClick={() => addPassenger()}
                              >
                                Agregar otro pasajero
                              </Button>
                            </Grid>
                          </Grid>
                        )}
                      </FormDataConsumer>
                    </CardCommon>
                  </>
                )}
                <CardCommon title={translate('ra.title.reserve_information')} style={{ marginBottom: 12 }}>
                  <Grid container spacing={3}>
                    <EditInputsWrap label={translate('ra.field.number')}>
                      <TextInput source='number' label='' fullWidth disabled />
                    </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.client')}>
                      <TextInput source='client' label='' fullWidth disabled />
                    </EditInputsWrap>
                    {!recordProduct && (
                      <EditInputsWrap label={translate('ra.field.amount')}>
                        <TextInput source='net_price' label='' fullWidth disabled />
                      </EditInputsWrap>
                    )}
                    <EditInputsWrap label={translate('ra.field.status')} required>
                      <SelectInput
                        source='status'
                        optionsText={['name']}
                        optionValue='key'
                        options={[
                          { key: CONFIRMED, name: 'CONFIRMADO' },
                          { key: CANCELLED, name: 'CANCELADO OFFLINE' },
                          { key: INVOICED, name: 'EMITIDA' },
                          { key: EXPIRED, name: 'EXPIRADA' },
                          { key: PENDING, name: 'PENDIENTE' },
                        ]}
                        label=''
                        fullWidth
                        validate={[required()]}
                        disabled={Boolean(retrieve)}
                      />
                    </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.source')} required>
                      {loadingSources ? (
                        <Skeleton variant='rectangular' height={40} />
                      ) : (
                        <AutocompleteInput
                          source='source'
                          options={sources}
                          validate={[required()]}
                          optionsText={['long_name']}
                          disableClearable
                          disabled={Boolean(retrieve)}
                        />
                      )}
                    </EditInputsWrap>
                    {!recordProduct && (
                      <EditInputsWrap label={translate('ra.field.passenger')} required>
                        <SelectInput
                          source='pax_titular'
                          optionsText={['name', 'surname']}
                          optionValue='id'
                          options={[
                            ...passengersAux,
                            ...aditionalPassengers.filter((elem) => elem.isNew && elem.name && elem.surname),
                          ]}
                          label=''
                          fullWidth
                          validate={[required()]}
                          separator=' '
                        />
                      </EditInputsWrap>
                    )}

                    <Typography
                      variant='h6'
                      component='div'
                      color={blue}
                      style={{ marginLeft: 12, marginBottom: 10, width: '100%' }}
                    >
                      {translate('ra.title.provider_info')}
                    </Typography>
                    <EditInputsWrap label={translate('ra.field.provider')} required>
                      {loadingSupplier ? (
                        <Skeleton variant='rectangular' height={40} />
                      ) : (
                        <SelectInput
                          source='supplier_id'
                          optionsText={['name']}
                          optionValue='id'
                          options={providers}
                          label=''
                          fullWidth
                          validate={[required()]}
                        />
                      )}
                    </EditInputsWrap>
                    {!recordProduct && (
                      <>
                        <EditInputsWrap label={`${translate('ra.field.locator')} 1`} required>
                          <TextInput source='booking_reference' label='' fullWidth validate={[required()]} />
                        </EditInputsWrap>
                        <EditInputsWrap label={`${translate('ra.field.locator')} 2`}>
                          <TextInput source='provider_code' label='' fullWidth />
                        </EditInputsWrap>
                      </>
                    )}

                    <EditInputsWrap label={translate('ra.field.currency')} required>
                      {loadingCurrencies ? (
                        <Skeleton variant='rectangular' height={40} />
                      ) : (
                        <SelectInput
                          source='currency_id'
                          optionsText={['long_name']}
                          optionValue='id'
                          options={currencies}
                          label=''
                          fullWidth
                          validate={[required()]}
                        />
                      )}
                    </EditInputsWrap>
                    {!recordProduct && (
                      <>
                        {!Boolean(retrieve) && (
                          <FormDataConsumer>
                            {({ formData }) => (
                              <EditInputsWrap label={translate('ra.field.net_provider')} required>
                                <NumberInput
                                  source='net_price'
                                  label=''
                                  fullWidth
                                  validate={[required()]}
                                  onChange={(e) => onChangeNetPrice(formData, e.target.value)}
                                />
                              </EditInputsWrap>
                            )}
                          </FormDataConsumer>
                        )}
                        <EditInputsWrap label={translate('ra.field.final_sale')}>
                          <NumberInput
                            source='final_sale'
                            label=''
                            fullWidth
                          />
                        </EditInputsWrap>
                        <Typography
                          variant='h6'
                          component='div'
                          color={blue}
                          style={{ marginLeft: 12, marginBottom: 10, width: '100%' }}
                        >
                          {translate('ra.title.tags')}
                        </Typography>
                        <EditInputsWrap label={translate('ra.field.add_tags')} md={12} className='small'>
                          {loadingTags ? (
                            <Skeleton variant='rectangular' height={40} />
                          ) : (
                            <AutocompleteInput
                              source='tag_relation'
                              multiple
                              options={tags}
                              optionsText={['long_name']}
                              renderOption={renderOption}
                              renderTags
                            />
                          )}
                        </EditInputsWrap>
                      </>
                    )}
                  </Grid>
                </CardCommon>
                <CardCommon title={translate('ra.title.train_information')} style={{ marginBottom: 12 }}>
                  <Grid container spacing={3} columns={24}>
                    {!recordProduct && (
                      <>
                        <FormDataConsumer>
                          {({ formData }) => (
                            <>
                              {retrieve && (
                                <Grid item xs={24} className='showCard'>
                                  <CompleteSideCard style={{ display: 'block' }}>
                                    <Box display={'flex'}>
                                      <LeftSideCard>
                                        <ShowElement
                                          data={retrieve.expiration_datetime}
                                          styleValue={{
                                            fontWeight: 600,
                                            backgroundColor: 'red',
                                            borderRadius: '10px',
                                            padding: '0 5px 0 5px',
                                          }}
                                          dateFormatCustom={getDay}
                                          label={translate('ra.field.expiration')}
                                          icon={<CalendarMonthOutlined style={{ paddingTop: '5px' }} />}
                                        />
                                        <ShowElement
                                          data={retrieve.arrival_name}
                                          styleValue={{ fontWeight: 600, color: turquoise }}
                                          label={translate('ra.field.destination')}
                                          icon={
                                            <LocationOnOutlined
                                              style={{ color: turquoise, paddingTop: '5px' }}
                                            />
                                          }
                                        />
                                        <ShowElement
                                          data={retrieve.products?.[0]?.label}
                                          label={translate('ra.field.product')}
                                        />
                                        <DateInDateOutField data={retrieve} />
                                        <ShowElement
                                          data={retrieve.period_type?.description}
                                          label={translate('ra.field.period_type')}
                                        />
                                        <ShowElement
                                          data={retrieve.duration?.description}
                                          label={translate('ra.field.duration')}
                                        />
                                        <ShowElement
                                          data={retrieve.rate_type}
                                          label={translate('ra.field.rate_type')}
                                        />
                                        <ShowElement
                                          data={retrieve.products?.[0]?.marketingCarrier}
                                          label={translate('ra.field.marketingCarrier')}
                                        />
                                        <ShowElement
                                          data={retrieve.products?.[0]?.supplier}
                                          label={translate('ra.field.supplier')}
                                        />
                                      </LeftSideCard>
                                      <RigthSideCard>
                                        <TrainPassesExtraPassenger data={retrieve} />
                                      </RigthSideCard>
                                    </Box>
                                    <TrainPassesCondition
                                      open={openModalCondition}
                                      handleClose={() => setOpenModalCondition(false)}
                                      data={retrieve}
                                    />
                                    <Button
                                      onClick={() => setOpenModalCondition(true)}
                                      style={{ fontWeight: 600, textDecoration: 'underline' }}
                                    >
                                      {translate('ra.action.see_conditions')}
                                    </Button>
                                  </CompleteSideCard>
                                </Grid>
                              )}

                              <EditInputsWrap
                                label={translate('ra.field.titular_passenger')}
                                required
                                xs={24}
                                md={8}
                                style={{ marginTop: 20 }}
                              >
                                <SelectInput
                                  source='pax_info'
                                  optionsText={['name', 'surname']}
                                  optionValue='id'
                                  options={[
                                    ...passengersAux,
                                    ...aditionalPassengers.filter(
                                      (elem) => elem.isNew && elem.name && elem.surname
                                    ),
                                  ]}
                                  label=''
                                  fullWidth
                                  validate={[required()]}
                                  separator=' '
                                  defaultValue={passengers?.find(elem => elem.is_titular)?.id || passengers?.[0]?.id || ''}
                                  onChange={(e) => onChangeTitularPassenger(e.target.value, formData)}
                                />
                              </EditInputsWrap>
                              <Hidden mdDown>
                                <Grid item md={16} />
                              </Hidden>
                              {formData.pax_info &&
                                PASSENGER_FIELDS.map((field, j) => (
                                  <EditInputsWrap label={translate(`ra.field.${field}`)} xs={24} md={6} key={j}>
                                    <TextField
                                      label=''
                                      size='small'
                                      fullWidth
                                      disabled
                                      value={getPassengerInfo(formData.pax_info, field)}
                                    />
                                  </EditInputsWrap>
                                ))}
                            </>
                          )}
                        </FormDataConsumer>
                      </>
                    )}
                    <Typography
                      variant='h6'
                      component='div'
                      color={blue}
                      style={{ marginLeft: 12, marginBottom: 10, width: '100%' }}
                    >
                      {translate('ra.title.cancellation_cost')}
                    </Typography>
                    <Typography
                      variant='h6'
                      component='div'
                      style={{ marginLeft: 12, marginBottom: 10, width: '100%', fontSize: '1em' }}
                    >
                      {translate('ra.title.cancellation_cost_provider')}
                    </Typography>
                    <EditInputsWrap label={translate('ra.field.date')} required xs={24} md={8}>
                      <DatePickers source='cancellation_date' required viewsShow={false} />
                    </EditInputsWrap>
                    <EditInputsWrap label={translate('ra.field.amount')} required xs={24} md={8}>
                      <NumberInput source='cancellation_amount' label='' fullWidth validate={[required()]} />
                    </EditInputsWrap>
                  </Grid>
                </CardCommon>
                <HasPermission resource='file-upload' permission={PERMISSION_GET}>
                  <CardCommon title={translate('ra.title.files')}>
                    <FilesCard ref={filesCardRef} recordProduct={recordProduct} />
                  </CardCommon>
                </HasPermission>
              </Grid>
              {!recordProduct && (
                <Grid item xs={3}>
                  <Grid position='sticky' top={0}>
                    <FormDataConsumer>
                      {({ formData }) => {
                        const isDisabled =
                          loadingLiquidation ||
                          !workUnit ||
                          !product ||
                          !retrieve ||
                          !(formData.net_price !== null && formData.net_price !== undefined) ||
                          !formData.currency_id
                        return (
                          <>
                            <Grid display='flex' justifyContent='flex-end' marginBottom='12px'>
                              <Tooltip
                                title='Para calcular la liquidación, es necesario rellenar los siguientes campos: Moneda, Proveedor, Neto proveedor y recuperar datos del proveedor'
                                className='tooltip'
                                placement='top'
                              >
                                <Icon>info</Icon>
                              </Tooltip>
                              <Button
                                variant='contained'
                                disabled={isDisabled}
                                onClick={() => onClickCalculateLiquidation(formData)}
                              >
                                {translate('ra.action.calculate_liquidation')}
                              </Button>
                            </Grid>
                            <CardCommonExpandible
                              title={translate('ra.title.liquidation_wholesaler')}
                              className='small-fields'
                              style={{ marginBottom: 12 }}
                            >
                              <Grid container spacing={3}>
                                {formData?.pricing?.liquidation_wholesaler.messsageArg?.length > 0 ? (
                                  <Typography padding={'20px'} style={{ color: 'red' }}>
                                    {translate(formData?.pricing?.liquidation_wholesaler?.message, {
                                      rule: formData?.pricing?.liquidation_wholesaler?.messsageArg,
                                    })}
                                  </Typography>
                                ) : wholesalerLiquidation && wholesalerLiquidation.length > 0 ? (
                                  wholesalerLiquidation.map((item, index) => (
                                    <TableRow key={index} style={{ display: 'flex', flex: '1 1 100%' }}>
                                      <TableCell style={{ flex: 1, alignItems: 'center', display: 'flex' }}>
                                        {item?.description || item.name}
                                      </TableCell>
                                      <TableCell>
                                        <ShowElement
                                          data={numberFormatterWithDecimalAndThousand(item.value)}
                                          onlyValue
                                          blueText
                                          monetarySymbol={
                                            currencies.find((elem) => elem.id === formData.currency_id)
                                              ?.symbol || ''
                                          }
                                          styleValue={{
                                            fontSize: '16px',
                                            fontFamily: "'Roboto Condensed', sans-serif",
                                          }}
                                          source={undefined}
                                          label={undefined}
                                          date={undefined}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <Typography padding={'20px'}>
                                    {translate('ra.notification.liquidation_rule_invalid')}
                                  </Typography>
                                )}
                              </Grid>
                            </CardCommonExpandible>
                            <CardCommonExpandible
                              title={translate('ra.title.liquidation_retail')}
                              className='small-fields'
                              style={{ marginBottom: 12 }}
                            >
                              <Grid container spacing={3}>
                                {formData?.pricing?.liquidation_retail.messsageArg?.length > 0 ? (
                                  <Typography padding={'20px'} style={{ color: 'red' }}>
                                    {translate(formData?.pricing?.liquidation_retail?.message, {
                                      rule: formData?.pricing?.liquidation_retail?.messsageArg,
                                    })}
                                  </Typography>
                                ) : retailLiquidation && retailLiquidation.length > 0 ? (
                                  retailLiquidation.map((item, index) => (
                                    <TableRow key={index} style={{ display: 'flex', flex: '1 1 100%' }}>
                                      <TableCell style={{ flex: 1, alignItems: 'center', display: 'flex' }}>
                                        {item?.description || item.name}
                                      </TableCell>
                                      <TableCell>
                                        <ShowElement
                                          data={numberFormatterWithDecimalAndThousand(item.value)}
                                          onlyValue
                                          blueText
                                          monetarySymbol={
                                            currencies.find((elem) => elem.id === formData.currency_id)
                                              ?.symbol || ''
                                          }
                                          styleValue={{
                                            fontSize: '16px',
                                            fontFamily: "'Roboto Condensed', sans-serif",
                                          }}
                                          source={undefined}
                                          label={undefined}
                                          date={undefined}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <Typography padding={'20px'}>
                                    {translate('ra.notification.liquidation_rule_invalid')}
                                  </Typography>
                                )}
                              </Grid>
                            </CardCommonExpandible>
                            <CardCommonExpandible
                              title={translate('ra.title.liquidation_supplier')}
                              className='small-fields'
                              style={{ marginBottom: 12 }}
                            >
                              <Grid container spacing={3}>
                                {formData?.pricing?.liquidation_supplier.messsageArg?.length > 0 ? (
                                  <Typography padding={'20px'} style={{ color: 'red' }}>
                                    {translate(formData?.pricing?.liquidation_supplier?.message, {
                                      rule: formData?.pricing?.liquidation_supplier?.messsageArg,
                                    })}
                                  </Typography>
                                ) : supplierLiquidation && supplierLiquidation.length > 0 ? (
                                  supplierLiquidation.map((item, index) => (
                                    <TableRow key={index} style={{ display: 'flex', flex: '1 1 100%' }}>
                                      <TableCell style={{ flex: 1, alignItems: 'center', display: 'flex' }}>
                                        {item?.description || item.name}
                                      </TableCell>
                                      <TableCell>
                                        <ShowElement
                                          data={numberFormatterWithDecimalAndThousand(item.value)}
                                          onlyValue
                                          blueText
                                          monetarySymbol={
                                            currencies.find((elem) => elem.id === formData.currency_id)
                                              ?.symbol || ''
                                          }
                                          styleValue={{
                                            fontSize: '16px',
                                            fontFamily: "'Roboto Condensed', sans-serif",
                                          }}
                                          source={undefined}
                                          label={undefined}
                                          date={undefined}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <Typography padding={'20px'}>
                                    {translate('ra.notification.liquidation_rule_invalid')}
                                  </Typography>
                                )}
                              </Grid>
                            </CardCommonExpandible>
                          </>
                        )
                      }}
                    </FormDataConsumer>
                    <CardCommon title={translate('ra.title.contact')} className='small-fields'>
                      <Grid container spacing={3}>
                        <EditInputsWrap label={translate('ra.field.name')} md={12}>
                          <TextInput source='contact.name' label='' fullWidth />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.surname')} md={12}>
                          <TextInput source='contact.surname' label='' fullWidth />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.email')} md={12}>
                          <TextInput source='contact.email' label='' fullWidth />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.phone_number')} md={12}>
                          <TextInput source='contact.phone_number' label='' fullWidth />
                        </EditInputsWrap>
                        <EditInputsWrap label={translate('ra.field.observations')} md={12}>
                          <TextInput validate={maxLength(400)} source='observations' label='' fullWidth />
                        </EditInputsWrap>
                      </Grid>
                    </CardCommon>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions className='dialog-actions'>
            <Grid>
              <FormDataConsumer>
                {({ formData }) => {
                  const isDisabled =
                    loadingLiquidation ||
                    !workUnit ||
                    !product ||
                    !retrieve ||
                    !(formData.net_price !== null && formData.net_price !== undefined) ||
                    !formData.currency_id

                  return (
                    <>
                      <LoadingButton
                        loading={loadingButton}
                        variant='contained'
                        startIcon={<Save />}
                        form='post_create_form'
                        type='submit'
                        disabled={!recordProduct && isDisabled}
                      >
                        {recordProduct
                          ? translate('ra.action.save_and_recalculate_liquidation')
                          : translate('ra.action.save_and_calculate_liquidation')}
                      </LoadingButton>

                      {!recordProduct && isDisabled && (
                        <Tooltip
                          title='Para calcular la liquidación, es necesario rellenar los siguientes campos: Moneda, Proveedor, Neto proveedor y recuperar datos del proveedor'
                          className='tooltip'
                          placement='top'
                        >
                          <Icon>info</Icon>
                        </Tooltip>
                      )}
                    </>
                  )
                }}
              </FormDataConsumer>
            </Grid>
            <Button onClick={handleClose} disabled={loadingButton} variant='outlined' color='error'>
              {translate('ra.action.cancel')}
            </Button>
          </DialogActions>
      </Dialog>
    </Form>
  )
}
